import { useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import {
  usePatientInsurancesQuery,
  useCreatePatientInsuranceMutation,
  useUpdatePatientInsuranceMutation,
  useDeletePatientInsuranceMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import {
  otherInsuranceFormSchema,
  OtherInsuranceEntryFormShape,
} from '../schemas';
import { paths } from '@common/types';
import { isCashPaymentType, doPeriodsOverlap } from '../utils';

type FieldKey = 'primary_other' | 'secondary';

type CreateInsuranceBody =
  paths['/patient/{patient_id}/insurances']['post']['requestBody']['content']['application/json'];

type UpdateInsuranceBody =
  paths['/patient/{patient_id}/insurances/{insurance_id}']['patch']['requestBody']['content']['application/json'];

const fieldKeyLabel: Record<FieldKey, string> = {
  primary_other: 'Primary insurance',
  secondary: 'Secondary insurance',
};

const isCash = (entry: OtherInsuranceEntryFormShape) =>
  isCashPaymentType(entry.payment_type);

const toCreateBody = (
  entry: OtherInsuranceEntryFormShape,
  type: FieldKey,
): CreateInsuranceBody => ({
  type,
  ...(!isCash(entry) && {
    insurance_company: entry.insurance_company,
    insurance_id: entry.insurance_id,
    mbi: entry.mbi || undefined,
    group_number: entry.group_number || undefined,
    plan_name: entry.plan_name || undefined,
    subscriber: {
      is_patient: entry.subscriber?.is_patient === 'true',
      relationship: entry.subscriber?.relationship as never,
      subscriber_id: entry.subscriber?.subscriber_id || undefined,
      name:
        entry.subscriber?.is_patient === 'false'
          ? (entry.subscriber?.relationship ?? '')
          : undefined,
    },
  }),
  start_date: entry.start_date ?? '',
  payment_type: entry.payment_type as never,
  end_date: entry.end_date || null,
  claim_address: { source: 'stored' },
  phone: { source: 'stored' },
  consent: {
    verbal_consent_date:
      entry.consent?.verbal_consent_date &&
      entry.consent.verbal_consent_date.length > 0
        ? entry.consent.verbal_consent_date
        : null,
    given_by: entry.consent?.given_by || undefined,
    witness: entry.consent?.witness || undefined,
  },
  comments: entry.comments || undefined,
});

const toUpdateBody = (
  entry: OtherInsuranceEntryFormShape,
): UpdateInsuranceBody => ({
  ...(!isCash(entry) && {
    insurance_company: entry.insurance_company,
    insurance_id: entry.insurance_id,
    mbi: entry.mbi || undefined,
    group_number: entry.group_number || undefined,
    plan_name: entry.plan_name || undefined,
    subscriber: {
      is_patient: entry.subscriber?.is_patient === 'true',
      relationship: entry.subscriber?.relationship as never,
      subscriber_id: entry.subscriber?.subscriber_id || undefined,
      name:
        entry.subscriber?.is_patient === 'false'
          ? (entry.subscriber?.relationship ?? '')
          : undefined,
    },
  }),
  start_date: entry.start_date ?? '',
  payment_type: entry.payment_type as never,
  end_date: entry.end_date || null,
  claim_address: { source: 'stored' },
  phone: { source: 'stored' },
  consent: {
    verbal_consent_date:
      entry.consent?.verbal_consent_date &&
      entry.consent.verbal_consent_date.length > 0
        ? entry.consent.verbal_consent_date
        : null,
    given_by: entry.consent?.given_by || undefined,
    witness: entry.consent?.witness || undefined,
  },
  comments: entry.comments || undefined,
});

export const defaultOtherEntry: OtherInsuranceEntryFormShape = {
  id: undefined,
  insurance_company: '',
  insurance_company_name: '',
  insurance_id: '',
  start_date: '',
  payment_type: 'insurance',
  mbi: '',
  group_number: '',
  plan_name: '',
  end_date: '',
  subscriber: { is_patient: 'true', relationship: '', subscriber_id: '' },
  consent: { verbal_consent_date: '', given_by: '', witness: '' },
  comments: '',
};

export type OtherInsuranceFormShape = {
  entries: OtherInsuranceEntryFormShape[];
};

const formSchema = otherInsuranceFormSchema;

const buildValues = (
  records: NonNullable<ReturnType<typeof usePatientInsurancesQuery>['data']>[
    'primary_other' | 'secondary'],
): OtherInsuranceFormShape => ({
  entries:
    records.length > 0
      ? records.map(r => ({
          id: r.id,
          insurance_company: r.insurance_company ?? '',
          insurance_company_name: r.insurance_company_name ?? '',
          insurance_id: r.insurance_id ?? '',
          mbi: r.mbi ?? '',
          group_number: r.group_number ?? '',
          plan_name: r.plan_name ?? '',
          payment_type: r.payment_type ?? '',
          start_date: r.start_date ?? '',
          end_date: r.end_date ?? '',
          subscriber: {
            is_patient: r.subscriber?.is_patient === false ? 'false' : 'true',
            relationship: r.subscriber?.relationship ?? '',
            subscriber_id: r.subscriber?.subscriber_id ?? '',
          },
          consent: {
            verbal_consent_date: r.consent?.verbal_consent_date ?? '',
            given_by: r.consent?.given_by ?? '',
            witness: r.consent?.witness ?? '',
          },
          comments: r.comments ?? '',
        }))
      : [defaultOtherEntry],
});

export const useOtherInsuranceForm = (
  patient_id: string,
  fieldKey: FieldKey,
) => {
  const { data: patientInsurances } = usePatientInsurancesQuery({ patient_id });

  const { mutateAsync: createInsurance } = useCreatePatientInsuranceMutation({
    patient_id,
  });
  const { mutateAsync: updateInsurance } = useUpdatePatientInsuranceMutation({
    patient_id,
  });
  const { mutateAsync: deleteInsurance } = useDeletePatientInsuranceMutation({
    patient_id,
  });

  const formMethods = useForm<OtherInsuranceFormShape>({
    mode: 'all',
    resolver: yupResolver(formSchema as never),
    defaultValues: { entries: [defaultOtherEntry] },
  });

  const {
    reset,
    handleSubmit,
    control,
    formState: { isDirty, isSubmitting, isValid },
  } = formMethods;

  const watchedEntries = useWatch({ control, name: 'entries' });

  const hasOverlapError =
    watchedEntries.length >= 2 &&
    watchedEntries.some((entry, i) => {
      if (!entry.start_date || !entry.payment_type) return false;
      return watchedEntries.some((other, j) => {
        if (i === j) return false;
        if (!other.start_date || !other.payment_type) return false;
        if (other.payment_type !== entry.payment_type) return false;
        return doPeriodsOverlap(
          entry.start_date!,
          entry.end_date || undefined,
          other.start_date!,
          other.end_date || undefined,
        );
      });
    });

  const pendingDraftEntriesRef = useRef<OtherInsuranceEntryFormShape[]>([]);

  const resetForm = useCallback(() => {
    const records = patientInsurances?.[fieldKey] ?? [];
    const built = buildValues(records);
    const drafts = pendingDraftEntriesRef.current;
    if (drafts.length > 0) {
      pendingDraftEntriesRef.current = [];
      reset({ entries: [...built.entries, ...drafts] });
    } else {
      reset(built);
    }
  }, [patientInsurances, fieldKey, reset]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const isEntryDirty = useCallback(
    (entry: OtherInsuranceEntryFormShape): boolean => {
      const originals = patientInsurances?.[fieldKey] ?? [];
      if (!entry.id) return true;
      const orig = originals.find(r => r.id === entry.id);
      if (!orig) return true;
      return (
        (entry.insurance_company ?? '') !== (orig.insurance_company ?? '') ||
        entry.insurance_id !== (orig.insurance_id ?? '') ||
        entry.mbi !== (orig.mbi ?? '') ||
        entry.group_number !== (orig.group_number ?? '') ||
        entry.plan_name !== (orig.plan_name ?? '') ||
        entry.payment_type !== (orig.payment_type ?? '') ||
        entry.start_date !== (orig.start_date ?? '') ||
        entry.end_date !== (orig.end_date ?? '') ||
        entry.comments !== (orig.comments ?? '') ||
        entry.subscriber?.is_patient !==
          (orig.subscriber?.is_patient === false ? 'false' : 'true') ||
        entry.subscriber?.relationship !==
          (orig.subscriber?.relationship ?? '') ||
        entry.subscriber?.subscriber_id !==
          (orig.subscriber?.subscriber_id ?? '') ||
        entry.consent?.verbal_consent_date !==
          (orig.consent?.verbal_consent_date ?? '') ||
        entry.consent?.given_by !== (orig.consent?.given_by ?? '') ||
        entry.consent?.witness !== (orig.consent?.witness ?? '')
      );
    },
    [patientInsurances, fieldKey],
  );

  const getInsuranceDisplayName = useCallback(
    (entry: OtherInsuranceEntryFormShape): string => {
      const name = entry.insurance_company_name || entry.insurance_id;
      return name
        ? `${fieldKeyLabel[fieldKey]} (${name})`
        : fieldKeyLabel[fieldKey];
    },
    [fieldKey],
  );

  const saveSingleEntry = useCallback(
    async (entry: OtherInsuranceEntryFormShape): Promise<boolean> => {
      const displayName = getInsuranceDisplayName(entry);
      const isNew = !entry.id;
      try {
        if (entry.id) {
          await updateInsurance({ id: entry.id, ...toUpdateBody(entry) });
        } else {
          await createInsurance(toCreateBody(entry, fieldKey));
        }
        toast.success(
          `${displayName} ${isNew ? 'created' : 'updated'} successfully`,
        );
        return true;
      } catch (error) {
        toast.error(
          `${displayName} ${isNew ? 'create' : 'update'} failed: ${formatApiError(error)}`,
        );
        return false;
      }
    },
    [fieldKey, createInsurance, updateInsurance, getInsuranceDisplayName],
  );

  const submitHandler = useCallback(
    async (values: OtherInsuranceFormShape) => {
      const changedEntries = values.entries.filter(entry =>
        isEntryDirty(entry),
      );

      if (changedEntries.length === 0) return true;

      const results = await Promise.allSettled(
        changedEntries.map(entry => saveSingleEntry(entry)),
      );

      const hasError = results.some(
        r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value),
      );

      if (!hasError) {
        reset(values);
      }
      return !hasError;
    },
    [isEntryDirty, saveSingleEntry, reset],
  );

  const submit = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        handleSubmit(
          async values => {
            resolve(await submitHandler(values));
          },
          () => {
            resolve(false);
          },
        )();
      }),
    [handleSubmit, submitHandler],
  );

  const submitEntry = useCallback(
    (index: number) =>
      new Promise<boolean>(resolve => {
        handleSubmit(
          async values => {
            const entry = values.entries[index];
            if (!entry) {
              resolve(false);
              return;
            }
            if (!isEntryDirty(entry)) {
              resolve(true);
              return;
            }
            const success = await saveSingleEntry(entry);
            if (success) {
              reset(values);
            }
            resolve(success);
          },
          () => {
            resolve(false);
          },
        )();
      }),
    [handleSubmit, isEntryDirty, saveSingleEntry, reset],
  );

  const deleteEntry = useCallback(
    async (id: string | undefined) => {
      if (!id) return;
      try {
        const draftEntries = formMethods
          .getValues('entries')
          .filter(e => !e.id);

        if (draftEntries.length > 0) {
          pendingDraftEntriesRef.current = draftEntries;
        }

        await deleteInsurance({ insurance_id: id });
        toast.success('Insurance deleted successfully');
      } catch (error) {
        pendingDraftEntriesRef.current = [];
        toast.error(`Delete failed: ${formatApiError(error)}`);
        throw error;
      }
    },
    [deleteInsurance, formMethods],
  );

  return {
    formMethods,
    isDirty,
    isValid: isValid && !hasOverlapError,
    isSubmitting,
    resetForm,
    submit,
    submitEntry,
    deleteEntry,
  };
};
